import { useEffect, useId, useRef } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Alert, Button, Icon } from '@chikara/ui';
import { t } from '@/i18n';
import styles from './SwitchCompanyModal.module.css';

export interface SwitchCompanyModalProps {
  isOpen: boolean;
  currentCompanyName: string;
  targetCompanyName: string;
  isLoading: boolean;
  errorMessage?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

function ArrowCircle() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle cx="16" cy="16" r="16" fill="var(--color-brand-light)" />
      <path
        d="M9 16H23M23 16L17.5 10.5M23 16L17.5 21.5"
        stroke="var(--color-primary)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SwitchCompanyModal({
  isOpen,
  currentCompanyName,
  targetCompanyName,
  isLoading,
  errorMessage,
  onConfirm,
  onClose,
}: SwitchCompanyModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const isLoadingRef = useRef(isLoading);
  onCloseRef.current = onClose;
  isLoadingRef.current = isLoading;

  const requestClose = () => {
    if (!isLoadingRef.current) onCloseRef.current();
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!isLoadingRef.current) onCloseRef.current();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => previouslyFocused?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const body = document.body;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const handleDialogKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab' || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled])');
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) {
      event.preventDefault();
      return;
    }
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className={styles.overlay}
      onClick={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleDialogKeyDown}
      >
        <header className={styles.header}>
          <button type="button" className={styles.close} onClick={requestClose} aria-label={t('companies.confirm.close')} disabled={isLoading}>
            <Icon name="close" size="sm" color="inherit" />
          </button>
          <h2 id={titleId} className={styles.title}>
            {t('companies.confirm.title')}
          </h2>
        </header>
        <div className={styles.content}>
          <div className={styles.transition}>
            <span className={`${styles.caption} ${styles.current}`}>{t('companies.confirm.current')}</span>
            <strong className={`${styles.name} ${styles.current}`}>{currentCompanyName}</strong>
            <span className={styles.arrow}>
              <ArrowCircle />
            </span>
            <span className={`${styles.caption} ${styles.next}`}>{t('companies.confirm.next')}</span>
            <strong className={`${styles.name} ${styles.next}`}>{targetCompanyName}</strong>
          </div>
          {errorMessage ? <Alert variant="error">{errorMessage}</Alert> : null}
          <div className={styles.actions}>
            <Button variant="secondary" className={styles.action} onClick={onClose} disabled={isLoading}>
              {t('companies.confirm.cancel')}
            </Button>
            <Button variant="primary" className={styles.action} onClick={onConfirm} isLoading={isLoading}>
              {t('companies.confirm.confirm')}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
